import { memo, type CSSProperties } from 'react';
import { hexA, type Item } from '@scribbleplot/shared';
import { typography } from './typography';
import { color, font, radius, shadow } from '../tokens';

/**
 * Canvas item renderers.
 *
 * Items live in world coordinates inside a single scaled container, so nothing
 * here needs to know about the camera — the parent transform handles zoom. Each
 * item is memoised, because a drag re-renders the moving items only.
 */

export type ItemViewProps = {
  item: Item;
  selected: boolean;
  accent: string;
  /** Selection ring width is divided by scale so it stays 1.6px on screen. */
  scale: number;
  /** True while this item's text is being edited, so the static copy hides. */
  editing: boolean;
  onPointerDown: (event: React.PointerEvent) => void;
  onDoubleClick: (event: React.MouseEvent) => void;
  onContextMenu: (event: React.MouseEvent) => void;
};

const ringFor = (accent: string, scale: number) =>
  `0 0 0 ${(1.6 / scale).toFixed(2)}px ${accent}, 0 0 24px ${hexA(accent, 0.22)}`;

/**
 * Below this on-screen width an item is rendered as a plain block.
 *
 * At a few pixels across, text is unreadable and shadows are invisible, but the
 * browser still pays full price for laying them out — which is what makes a
 * zoomed-out board of thousands of items expensive. Colour and position are the
 * only things that still carry meaning at that size, so that is all we draw.
 */
const LOD_MIN_PX = 26;

/** Colour that stands in for an item when it is too small to draw properly. */
function lodColor(item: Item, accent: string): string {
  switch (item.type) {
    case 'sticky':
      return item.color;
    case 'group':
      return accent;
    case 'text':
      return '#3E4250';
    case 'link':
      return '#2A2C33';
    default:
      return '#4A4E5C';
  }
}

/** Simplified stand-in for an item that is only a few pixels wide on screen. */
function LodBlock({ item, selected, accent, scale, onPointerDown, onContextMenu }: ItemViewProps) {
  return (
    <div
      data-item-id={item.id}
      data-item-type={item.type}
      onPointerDown={onPointerDown}
      onContextMenu={onContextMenu}
      // Decorative at this size; the layers panel is the accessible listing.
      aria-hidden
      style={{
        position: 'absolute',
        left: item.x,
        top: item.y,
        width: item.w,
        height: item.h,
        zIndex: item.z,
        background: lodColor(item, accent),
        borderRadius: 12,
        outline: selected ? `${(1.6 / scale).toFixed(2)}px solid ${accent}` : 'none',
        cursor: 'default',
      }}
    />
  );
}

function FrameView({ item, selected, accent, scale, onPointerDown, onDoubleClick, onContextMenu }: ItemViewProps) {
  if (item.type !== 'frame') return null;
  return (
    <>
      {/* The frame body never takes pointer events — clicks fall through to
          whatever sits inside it. Only the label is a drag handle. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: item.x,
          top: item.y,
          width: item.w,
          height: item.h,
          zIndex: item.z,
          background: 'rgba(255,255,255,.032)',
          border: selected ? `1.5px solid ${hexA(accent, 0.85)}` : `1px solid rgba(255,255,255,.1)`,
          borderRadius: 14,
          pointerEvents: 'none',
          boxShadow: selected ? `0 0 34px ${hexA(accent, 0.12)}` : 'none',
        }}
      />
      <div
        onPointerDown={onPointerDown}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
        title={item.label}
        data-item-id={item.id}
        data-item-type={item.type}
        style={{
          position: 'absolute',
          left: item.x,
          // Sits just above the frame, in world units, so it scales with zoom.
          top: item.y - 34,
          height: 26,
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
          background: color.panel,
          border: `1px solid rgba(255,255,255,${selected ? '.4' : '.14'})`,
          borderRadius: 7,
          fontFamily: font.mono,
          fontSize: 11,
          letterSpacing: '.09em',
          textTransform: 'uppercase',
          color: selected ? color.text : color.textMuted,
          cursor: 'grab',
          zIndex: item.z,
          whiteSpace: 'nowrap',
        }}
      >
        {item.label}
      </div>
    </>
  );
}

function StickyView({
  item,
  selected,
  accent,
  scale,
  editing,
  onPointerDown,
  onDoubleClick,
  onContextMenu,
}: ItemViewProps) {
  if (item.type !== 'sticky') return null;
  return (
    <div
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      data-item-id={item.id}
      data-item-type={item.type}
      style={{
        position: 'absolute',
        left: item.x,
        top: item.y,
        width: item.w,
        height: item.h,
        zIndex: item.z,
        background: item.color,
        borderRadius: 3,
        padding: 13,
        boxShadow: selected ? ringFor(accent, scale) : shadow.sticky,
        cursor: 'default',
      }}
    >
      <div
        style={{
          ...typography(item),
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          height: '100%',
          overflow: 'hidden',
          opacity: editing ? 0 : 1,
        }}
      >
        {item.text}
      </div>
    </div>
  );
}

function TextView({
  item,
  selected,
  accent,
  scale,
  editing,
  onPointerDown,
  onDoubleClick,
  onContextMenu,
}: ItemViewProps) {
  if (item.type !== 'text') return null;
  return (
    <div
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      data-item-id={item.id}
      data-item-type={item.type}
      style={{
        position: 'absolute',
        left: item.x,
        top: item.y,
        width: item.w,
        height: item.h,
        zIndex: item.z,
        padding: 6,
        borderRadius: 8,
        boxShadow: selected ? ringFor(accent, scale) : 'none',
        cursor: 'default',
      }}
    >
      <div
        style={{
          ...typography(item),
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          height: '100%',
          overflow: 'hidden',
          opacity: editing ? 0 : 1,
        }}
      >
        {item.text}
      </div>
    </div>
  );
}

function ImageView({ item, selected, accent, scale, onPointerDown, onContextMenu }: ItemViewProps) {
  if (item.type !== 'image') return null;
  return (
    <div
      onPointerDown={onPointerDown}
      onContextMenu={onContextMenu}
      title={item.name}
      data-item-id={item.id}
      data-item-type={item.type}
      style={{
        position: 'absolute',
        left: item.x,
        top: item.y,
        width: item.w,
        height: item.h,
        zIndex: item.z,
        borderRadius: 10,
        background: '#202127',
        boxShadow: selected ? ringFor(accent, scale) : shadow.card,
        cursor: 'default',
      }}
    >
      {/* A background image rather than an <img>: it cannot be dragged out of
          the canvas by the browser's native image drag. */}
      <div
        role="img"
        aria-label={item.name}
        style={{
          width: '100%',
          height: '100%',
          backgroundImage: `url("${item.src}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: 10,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

function GroupView({ item, selected, accent, scale, onPointerDown, onDoubleClick, onContextMenu }: ItemViewProps) {
  if (item.type !== 'group') return null;
  const thumbs = item.children.slice(0, 3);
  return (
    <div
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      title={`${item.name} — double-click to expand`}
      data-item-id={item.id}
      data-item-type={item.type}
      style={{
        position: 'absolute',
        left: item.x,
        top: item.y,
        width: item.w,
        height: item.h,
        zIndex: item.z,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px 10px 10px',
        background: color.raised,
        border: `1px solid ${color.lineStrong}`,
        borderRadius: 14,
        cursor: 'default',
        // Stacked edges read as "there is more inside this".
        boxShadow:
          (selected ? `${ringFor(accent, scale)}, ` : '') +
          '0 4px 0 -2px #1D1E23, 0 8px 0 -4px #191A1E, 0 14px 30px rgba(0,0,0,.4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', flex: 'none' }}>
        {thumbs.map((child, index) => {
          const base: CSSProperties = {
            width: 34,
            height: 34,
            borderRadius: 9,
            marginLeft: index ? -11 : 0,
            border: `2px solid ${color.raised}`,
            background: child.type === 'sticky' ? (child.color ?? '#2B2D35') : '#2B2D35',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontFamily: font.mono,
            color: color.textMuted,
            position: 'relative',
            zIndex: 3 - index,
            flex: 'none',
          };
          if (child.type === 'image' && child.src) {
            return (
              <div
                key={child.id}
                style={{
                  ...base,
                  backgroundImage: `url("${child.src}")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            );
          }
          const glyph = child.type === 'text' ? 'T' : child.type === 'link' ? 'L' : child.type === 'sticky' ? '' : '•';
          return (
            <div key={child.id} style={base}>
              {glyph}
            </div>
          );
        })}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {item.name}
        </div>
        <div style={{ fontSize: 11, color: color.textDim, marginTop: 1 }}>{item.children.length} items</div>
      </div>
    </div>
  );
}

function LinkView({ item, selected, accent, scale, onPointerDown, onDoubleClick, onContextMenu }: ItemViewProps) {
  if (item.type !== 'link') return null;
  return (
    <div
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      data-item-id={item.id}
      data-item-type={item.type}
      style={{
        position: 'absolute',
        left: item.x,
        top: item.y,
        width: item.w,
        height: item.h,
        zIndex: item.z,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 14px',
        background: color.panel,
        border: `1px solid rgba(255,255,255,.1)`,
        borderRadius: radius.lg,
        boxShadow: selected ? ringFor(accent, scale) : '0 10px 24px rgba(0,0,0,.3)',
        cursor: 'default',
      }}
    >
      <div
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: accent,
          boxShadow: `0 0 10px ${hexA(accent, 0.7)}`,
          flex: 'none',
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            fontSize: 11,
            fontFamily: font.mono,
            color: color.textDim,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginTop: 2,
          }}
        >
          {item.url}
        </div>
      </div>
    </div>
  );
}

export function isLodItem(item: Item, scale: number, editing: boolean): boolean {
  return item.type !== 'frame' && !editing && item.w * scale < LOD_MIN_PX && item.h * scale < LOD_MIN_PX;
}

export const ItemView = memo(function ItemView(props: ItemViewProps) {
  const { item, scale, editing } = props;

  /**
   * Frames keep their full rendering: there are few of them and their labels are
   * the only wayfinding left when zoomed out. An item being edited always draws
   * properly, however small — the editor is anchored to it.
   */
  if (isLodItem(item, scale, editing)) {
    return <LodBlock {...props} />;
  }

  switch (props.item.type) {
    case 'frame':
      return <FrameView {...props} />;
    case 'sticky':
      return <StickyView {...props} />;
    case 'text':
      return <TextView {...props} />;
    case 'image':
      return <ImageView {...props} />;
    case 'group':
      return <GroupView {...props} />;
    case 'link':
      return <LinkView {...props} />;
  }
});
